import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as StopIcon } from '../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { CommercialSessionInterface } from '../../../interface/charging-session.interface';
import { TransactionEMSPInterFace } from '../../../interface/emsp-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disablePaymentManagementApplied,
  fetchAllSession,
  fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
  refreshAllSessions,
} from '../../../redux/slices/EMSPCommercialSessionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomActionBar from '../../../shared-components/custom-action-bar/CustomActionBar';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../shared-components/custom-date-range/CustomDateRangePicker';
// import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import EmspCdrSidePanel from '../../../shared-components/table-side-panel/EmspCdrSidePanel';
import EmspSessionSidePanel from '../../../shared-components/table-side-panel/EmspSessionSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import {
  changeDateFormat,
  getDaysBetween,
  removeEmptyKeys,
} from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatDuration from '../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import occpEvent from '../../../utils/api/v2/emsp-session';

import './scss/index.scss';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Commercial Session Table Component
 */
function CommercialSessionTable() {
  const { t, i18n } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  // @ts-ignore
  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    TotalFinising,
    totalInprocess,
    totalFailed,
  } = useAppSelector((state) => state.EMSPCommercialSessionManagement);
  // const [isPolling, setIsPolling] = useState<boolean>(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    content: null,
    header: true,
    rightCorner: '',
    notification: false,
  });
  const [exportLoader, setExportLoader] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(['locationId']);
  const [activeStatus, setActiveStatus] = useState<string[]>([]);
  const [stopLoading, setStopLoading] = useState<boolean>(false);
  const [rowLoadingIds, setRowLoadingIds] = useState<Set<string>>(new Set());
  const isStatusClicked = React.useRef(false);
  const pageReference = React.useRef<number>(page || 1);

  useEffect(() => {
    if (page) {
      pageReference.current = page;
    }
  }, [page]);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('oneMonth'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });
  const [isAutoRefreshActive, setIsAutoRefreshActive] = useState<boolean>(false);

  /**
   * Function to clear the date range.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
    setIsAutoRefreshActive(false);
  };

  /**
   * Handle date range change.
   * @param {string} start - The start date string.
   * @param {string} end - The end date string.
   */
  const handleDateRangeChange = (start: string, end: string) => {
    const today = changeDateFormat(new Date().toString());
    setDateRange({ startDate: start, endDate: end });
    if (start !== today || end !== today) {
      setIsAutoRefreshActive(false);
    }
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="oneMonth"
          disableDates="future"
        />
      </div>
    );
  };

  // /**
  //  * Function to handle radio button change
  //  * @param {any} name - The name
  //  * @param {any} value - The value
  //  */
  // const handleAvailabilityChange = (name: string, value: string) => {
  //   setCurrentPage(value);
  // };

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };
  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   */
  const fetchSessions = (data: any) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(
        fetchAllSession({
          ...data,
          ...(activeStatus.length > 0 ? { 'status[]': activeStatus } : {}),
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({
      ...queryParameters,
      page,
      limit,
      ...sortValue,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: payments,
    disableEdit: true,
    disableDelete: true,
  });

  /**
   * Function to handle stop button click
   * @param {any} data session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = payments.filter((session: any) =>
        data.includes(session.sessionId),
      ) as unknown as CommercialSessionInterface[];
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const ids = itemsToProcess
      .map((item) => item.sessionId)
      .filter((id): id is string => Boolean(id));
    setStopLoading(true);
    setRowLoadingIds((previous) => {
      const next = new Set(previous);
      ids.forEach((id) => next.add(id));
      return next;
    });

    const promises = itemsToProcess.map((item: any) => {
      const payload = {
        // eslint-disable-next-line camelcase
        session_id: item.sessionId || item.session_id,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      })
      .finally(() => {
        setStopLoading(false);
        setRowLoadingIds((previous) => {
          const next = new Set(previous);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      });
  };

  const column = [
    {
      label: t('CDRId', { defaultValue: 'CDR ID' }),
      accessorKey: 'cdrId' as keyof TransactionEMSPInterFace,
      queryParamsKey: 'cdrObjectId',
      queryParamsValue: 'cdrId',
      sticky: 'left' as const,
      sidePanelComponent: (
        <EmspCdrSidePanel fetchIdFrom="queryParams" onActionSuccess={handleRefresh} />
      ),
      /**
       * Determines whether the side panel should be disabled for the row.
       * @param {TransactionEMSPInterFace} rowData - The current row data.
       * @returns {boolean} True if side panel is disabled.
       */
      isSidePanelDisabled: (rowData: TransactionEMSPInterFace) => !rowData.cdrId,
    },
    {
      label: t('sessionId') || 'Session Id',
      accessorKey: 'sessionId' as keyof TransactionEMSPInterFace,
      sticky: 'left' as const,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('deviceId'),
      accessorKey: 'evseUid' as keyof TransactionEMSPInterFace,
    },
    {
      label: `${t('sessionStartTime')} / ${t('sessionEndTime')}`,
      accessorKey: 'sessionEndAt' as keyof TransactionEMSPInterFace,
      /**
       * Format Session End Time
       * @param {TransactionEMSPInterFace} rowData - The row data
       * @returns Formatted date element
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <div className="session-time-column">
          {rowData.sessionStartAt ? ConvertUTCtoDateTime(rowData.sessionStartAt) : '-'}
          {' - '}
          {rowData.sessionEndAt ? ConvertUTCtoDateTime(rowData.sessionEndAt) : '-'}
        </div>
      ),
    },

    {
      label: t('locationId'),
      accessorKey: 'locationId' as keyof TransactionEMSPInterFace,
    },
    {
      label: t('evseUid'),
      accessorKey: 'evseUid' as keyof TransactionEMSPInterFace,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'energyKwh' as keyof TransactionEMSPInterFace,
      /**
       * Format Power Consumed
       * @param {TransactionEMSPInterFace} rowData - The row data
       * @returns Formatted power value
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        return <span>{ToFixedValue(rowData.energyKwh || 0, 3)} kWh</span>;
      },
    },
    {
      label: t('sessionAmount'),
      accessorKey: 'totalCost' as keyof TransactionEMSPInterFace,
      /**
       * Format Session Amount
       * @param {TransactionEMSPInterFace} rowData - The row data
       * @returns Formatted amount value
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        return (
          <span>
            {ToFixedValue(rowData.totalCost)} {rowData.currency}
          </span>
        );
      },
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof TransactionEMSPInterFace,
      /**
       * Rendering formatted duration
       * @param {TransactionEMSPInterFace} rowData Passing row data as a props
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{FormatDuration(Number(rowData.durationSeconds), { formatBy: 'sec' })}</span>
      ),
    },
    {
      label: t('driverName'),
      accessorKey: 'userName' as keyof TransactionEMSPInterFace,
      /**
       * Renders the driver's user ID with the driver's name stacked below it.
       * @param {TransactionEMSPInterFace} rowData - The row data
       * @returns {React.JSX.Element} Stacked user ID and driver name element
       */
      customData: (rowData: TransactionEMSPInterFace): React.JSX.Element => {
        const driverName = rowData.driver
          ? `${rowData.driver.firstName || ''} ${rowData.driver.lastName || ''}`.trim()
          : rowData.userName;

        const userId = rowData.driver?.userCustomId || rowData.userCustomId || '-';

        return (
          <div className="driver-info-column">
            <span className="driver-name" title={driverName || '-'}>
              {driverName || '-'}
            </span>

            <span className="driver-user-id" title={userId}>
              {userId}
            </span>
          </div>
        );
      },
    },
    {
      label: t('status') || 'Status',
      accessorKey: 'status' as keyof TransactionEMSPInterFace,
      /**
       * Custom data for the status column
       * @param {any} rowData - The rowData
       * @returns {any} The result
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        let statusClass = '';
        let translationKey = rowData?.status || '';

        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
            translationKey = 'finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
            translationKey = 'inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
            translationKey = 'pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
            translationKey = 'terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
            translationKey = 'failed';
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {rowData?.status ? translationKey : t('na')}
            </span>
          </span>
        );
      },
    },

    {
      label: t('actions'),
      accessorKey: '_id' as keyof TransactionEMSPInterFace,
      sticky: 'right' as const,

      /**
       * Custom data for the actions column
       * @param {TransactionEMSPInterFace} rowData session data
       * @returns JSX element
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        const isRowLoading = rowLoadingIds.has(rowData.sessionId || '');

        if (rowData.status === 'inProgress' && permissionCheck('mobilityActiveSessionStop')) {
          if (isRowLoading) {
            return (
              <div className="stop-buttons">
                <span className="row-action-spinner" title={t('stopping')}>
                  <span className="spinner-dot" />
                </span>
              </div>
            );
          }

          return (
            <div className="stop-buttons">
              <span title={t('stop')}>
                <StopIcon
                  onClick={() => handleStop(rowData as unknown as CommercialSessionInterface)}
                />
              </span>
            </div>
          );
        }

        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    if (value?.page) {
      pageReference.current = value.page;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({
      ...value,
      ...queryParameters,
      ...sortValue,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('sessionId'), accessorKey: 'sessionId' },
    { label: t('CDRId', { defaultValue: 'CDR ID' }), accessorKey: 'cdrId' },
    { label: t('sessionStartTime'), accessorKey: 'sessionStartAt' },
    { label: t('sessionEndTime'), accessorKey: 'sessionEndAt' },
    { label: t('deviceId'), accessorKey: 'evseUid' },
    { label: t('locationId'), accessorKey: 'locationId' },
    { label: t('evseUid'), accessorKey: 'evseUid' },
    { label: t('powerConsumed'), accessorKey: 'energyKwh' },
    { label: t('duration'), accessorKey: 'durationSeconds' },
    { label: t('userId'), accessorKey: 'userCustomId' },
    { label: t('driverName'), accessorKey: 'userName' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    occpEvent
      .fetchCommercialSessionExport(
        removeEmptyKeys({
          ...queryParameters,
          sortBy,
          sortField,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      )
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: TransactionEMSPInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'sessionStartAt':
                return row.sessionStartAt ? ConvertUTCtoDateTime(row.sessionStartAt) : '-';

              case 'sessionEndAt':
                return row.sessionEndAt ? ConvertUTCtoDateTime(row.sessionEndAt) : '-';

              case 'energyKwh':
                return `${ToFixedValue(row.energyKwh || 0, 3)} kWh`;

              case 'durationSeconds': {
                return FormatDuration(Number(row.durationSeconds), { formatBy: 'sec' });
              }

              case 'userCustomId':
                return row.driver?.userCustomId || row.userCustomId || '-';

              case 'userName':
                return row.driver
                  ? `${row.driver.firstName} ${row.driver.lastName}`
                  : row.userName || '-';

              default:
                return (row as any)[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'commercial-session-list.csv');
          } else {
            exportPDF(headers, rows, 'commercial-session-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);

        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: error?.response?.status || 500,
          }),
        );
      });
  };

  /**
   * Handle auto refresh toggle change
   * @param {boolean} nextState - Next auto refresh state
   */
  const handleAutoRefreshToggle = (nextState: boolean) => {
    setIsAutoRefreshActive(nextState);
    if (nextState) {
      const today = changeDateFormat(new Date().toString());
      if (dateRange.startDate !== today || dateRange.endDate !== today) {
        setDateRange({
          startDate: today,
          endDate: today,
        });
      }
    }
  };

  /**
   * Handle the refresh function
   */
  const handleAutoRefresh = () => {
    const today = changeDateFormat(new Date().toString());
    if (dateRange.startDate !== today || dateRange.endDate !== today) {
      return;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const activePage = pageReference.current || page || 1;

    dispatch(
      refreshAllSessions({
        ...queryParameters,
        page: activePage,
        limit,
        sortBy,
        sortField,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...(activeStatus.length > 0 ? { 'status[]': activeStatus } : {}),
      }),
    );
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      return;
    }

    isStatusClicked.current = false;
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchSessions({
      page: DefaultApiLimit.page,
      limit,
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    if (isFilterUpdated) {
      dispatch(disablePaymentManagementApplied());
    }
  }, [
    filters,
    activeStatus,
    dateRange.startDate,
    dateRange.endDate,
    limit,
    isFilterUpdated,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    /**
     * Handles payment action success event to refresh session list.
     */
    const handlePaymentActionSuccess = () => {
      handleRefresh();
    };

    window.addEventListener('emsp-payment-action-success', handlePaymentActionSuccess);
    return () => {
      window.removeEventListener('emsp-payment-action-success', handlePaymentActionSuccess);
    };
  }, [filters, dateRange.startDate, dateRange.endDate, page, limit, sortField, sortBy]);

  /**
   * Check if status filter is currently active
   * @param {string} statusValue - The status value to check
   * @returns {boolean} True if active
   */
  const isStatusActive = (statusValue: string): boolean => {
    return activeStatus.includes(statusValue);
  };

  /**
   * Handle status badge click to apply/toggle filter
   * @param {string} statusValue - The status value to filter by
   */
  const handleStatusClick = (statusValue: string) => {
    isStatusClicked.current = true;
    setActiveStatus((previous) =>
      previous.includes(statusValue)
        ? previous.filter((s) => s !== statusValue)
        : [...previous, statusValue],
    );
  };

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="stats-bar">
        <div className="stat-item" key="total">
          <span className="stat-label">{t('total')}</span>
          <span className="stat-value">{totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <div
          className={`stat-badge inProgress-badge ${isStatusActive('inProgress') ? 'active' : ''}`}
          key="inProgress"
          role="presentation"
          onClick={() => handleStatusClick('inProgress')}>
          <span className="badge-dot" />
          {t('inProgress')}: {totalInprocess ?? 0}
        </div>
        <div
          className={`stat-badge finished-badge ${isStatusActive('finished') ? 'active' : ''}`}
          key="finished"
          role="presentation"
          onClick={() => handleStatusClick('finished')}>
          <span className="badge-dot" />
          {t('finished')}: {TotalFinising ?? 0}
        </div>
        <div
          className={`stat-badge failed-badge ${isStatusActive('failed') ? 'active' : ''}`}
          key="failed"
          role="presentation"
          onClick={() => handleStatusClick('failed')}>
          <span className="badge-dot" />
          {t('failed')}: {totalFailed ?? 0}
        </div>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="CommercialSessions"
          version="v2"
          header={column}
          data={payments}
          limit={limit}
          page={pageReference.current || page || 1}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          created
          stickySerialNo
          lastUpdated
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          exportLoader={exportLoader}
          exportData={payments}
          exportDataPdfOrCSV={
            permissionCheck('mobilitySessionsExport') ? exportDataPdfOrCSV : undefined
          }
          hidedColumnNames={['locationId']}
          onHiddenColumnsChange={setHiddenColumns}
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPaymentMethodFilters}
          // rightTopContent={
          //   <div className="revenue-management-toggle-wrap">
          //     {' '}
          //     <CustomRadioButton
          //       name=""
          //       value="commercial"
          //       label={t('commercialCharger')}
          //       selected={currentPage === 'commercial'}
          //       onChangeFunc={handleAvailabilityChange}
          //     />
          //     <CustomRadioButton
          //       name=""
          //       value="personal"
          //       label={t('personalCharger')}
          //       selected={currentPage === 'personal'}
          //       onChangeFunc={handleAvailabilityChange}
          //     />
          //   </div>
          // }
          rightTopContent={<div className="table-bottom-button-list">{renderDateFilters()}</div>}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          // Trigger file compilation reload to clear stale ESLint cache
          height={320}
          requiredRefreshToggle
          refreshRequired
          autoRefreshEnabled
          isAutoRefreshActive={isAutoRefreshActive}
          onAutoRefreshChange={handleAutoRefreshToggle}
          refreshFunction={handleAutoRefresh}
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="sessionId"
          isRowSelectable={(row) => String(row.status || '').toLowerCase() === 'inprogress'}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'error',
              loading: stopLoading,
              /**
               * Handle stop — sets loading state while API call is in progress.
               */
              onClick: () => handleStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default CommercialSessionTable;
